import _ from 'lodash';
import React from 'react';
import cn from 'classnames';
import { connect, ConnectedProps } from 'react-redux';
import { createPortal } from 'react-dom';

import { ComponentTypes, StoryMediaPlatform } from 'types/story';
import { isLayerType } from 'common/utils/is-layer-type';
import { COMPONENT_TYPE } from 'common/constants';
import { ClientReducerState } from 'client/reducers';
import { setEditorSelectedNode } from 'client/actions';
import { SelectionHintController } from 'client/components/common/SelectionHint/SelectionHintController';
import { SelectionHintEvent } from 'client/components/common/SelectionHint/SelectionHintEvent';
import { Context as ResponsiveProviderContext } from 'client/components/common/ResponsiveProvider/Context';
import { ResponsiveProviderContextType } from 'client/components/common/ResponsiveProvider/types';
import { BOX_TYPE } from 'client/components/common/BuildingBlocks/Box';
import {
	SELECTION_STAGE,
	SELECTION_TYPES,
	getSelectionPosition,
	getSelectionGroupPosition,
} from 'client/components/common/SelectionHint/utils';
import Points from './Draggable/Points';
import { SUPPORTED_PROP } from './Draggable/Points/constants';
import Breadcrumbs from './Breadcrumbs';
import css from './SectionHint.scss';
import { SelectionSingleType } from './types';

const isChildOf = (selection: SelectionSingleType, parentType: typeof BOX_TYPE[keyof typeof BOX_TYPE]) => {
	const parent = selection.editableModeProps.inheritance?.parent;
	if (!parent) return false;
	const lastParent = _.last(parent);
	const boxType = lastParent?.inheritance.componentProps?.other?.boxType;
	if (!boxType) return false;
	return _.last(boxType)!.value === parentType;
};

const isStack = (props: SelectionSingleType) => props.uiConfig.componentProps?.other?.boxType === BOX_TYPE.STACK;
const isGrid = (props: SelectionSingleType) => props.uiConfig.componentProps?.other?.boxType === BOX_TYPE.GRID;
const isBBType = (props: SelectionSingleType, type: ComponentTypes) => props.type === type;

function isAllowToSelect({ selection, selectionType, componentProps, event }) {
	if (selectionType !== SELECTION_TYPES.clicked) {
		return true;
	}

	const clickedSelection = selection[SELECTION_TYPES.clicked];
	const isGlobal = isLayerType(componentProps).global;
	const isMultiSelect = event.shiftKey;
	const isAllowGlobal =
		_.size(clickedSelection) && isMultiSelect
			? _.values(clickedSelection).some(el => isLayerType(el).global)
			: isGlobal;

	return isAllowGlobal === isGlobal;
}

const isSelection = {
	typeOf: (selection, type) => selection.type === type,
	childOfType: (selection, type) =>
		selection.editableModeProps.inheritance.parent.some(parent => parent.type === type),
};

type State = {
	dragProp: string; //    dragProp property name (eg. size, position...)
	dragId: string; //      is of the draggable block
	isScrolling: boolean;
};

type Props = ConnectedProps<typeof connector>;

class SelectionHint extends React.Component<Props, State> {
	state = {
		dragProp: '',
		dragId: '',
		isScrolling: false,
	};

	scrollTimeout?: number;

	SelectionHintController?: SelectionHintController;

	SelectionHintEvent?: SelectionHintEvent;

	componentDidMount() {
		document.addEventListener('scroll', this.onScroll);
		window.addEventListener('resize', this.onScroll);

		this.SelectionHintController = new SelectionHintController({
			action: this.props.setEditorSelectedNode,
		});

		this.SelectionHintEvent = new SelectionHintEvent({
			listener: this.selectionHintEventListener,
		});

		this.SelectionHintEvent.addListener();
	}

	shouldComponentUpdate(nextProps: Props, nextState: State) {
		// shouldn't update while dragging
		if (Boolean(this.state.dragProp) && Boolean(nextState.dragProp)) return false;
		if (Boolean(this.state.dragProp) && !nextState.dragProp) return true;
		return true;
	}

	componentWillUnmount() {
		document.removeEventListener('scroll', this.onScroll);
		window.removeEventListener('resize', this.onScroll);
		this.SelectionHintEvent!.removeListener();
	}

	selectionHintEventListener = e => {
		if (!this.SelectionHintController) {
			console.error('this.SelectionHintController does not exists');
			return;
		}
		const selectionType = _.get(e, 'detail.selectionType');
		const componentProps = _.get(e, 'detail.componentProps');
		const originalEvent = _.get(e, 'detail.originalEvent');
		const trigger = _.get(e, 'detail.trigger');
		const stage = _.get(e, 'detail.stage');

		switch (trigger) {
			case SelectionHintController.FOCUS:
				this.SelectionHintController.focus(originalEvent, selectionType, componentProps);
				break;
			case SelectionHintController.SELECT:
				if (
					isAllowToSelect({
						selection: this.props.selection,
						componentProps,
						selectionType,
						event: originalEvent,
					})
				) {
					this.SelectionHintController.select(originalEvent, selectionType, componentProps, stage);
				}
				break;
			case SelectionHintController.DESELECT:
				this.SelectionHintController.deselect(originalEvent, selectionType);
				break;
			default:
				break;
		}
	};

	onScroll = event => {
		if (!this.scrollTimeout) {
			this.setState({ isScrolling: true });
		}
		window.clearTimeout(this.scrollTimeout);
		this.scrollTimeout = window.setTimeout(this.onScrollEnd, 100);
	};

	onScrollEnd = () => {
		delete this.scrollTimeout;
		this.setState({ isScrolling: false });
	};

	/**
	 * @param dragProp {String}
	 * @param dragId {String}
	 */
	setDragging = (dragProp: State['dragProp'], dragId: State['dragId']) => {
		this.setState({ dragProp, dragId });
	};

	/**
	 * Render draggable points to update selected element style on drag
	 * @param type {String}
	 * @param selection {Object} selection data object or object with a pair key value for each selection
	 * @param selectionStyle {Object}
	 * @param currentMediaQuery {String}
	 * @param selectionGroup {Object|null}
	 * @param isMultiple {Boolean}
	 * @param boundaries {Object} media query boundary values
	 * @return {null|*}
	 */
	renderPoints = ({
		type,
		selection,
		selectionStyle,
		currentMediaQuery,
		selectionGroup,
		isMultiple,
		boundaries,
	}: {
		type: keyof Props['selection'];
		selection: SelectionSingleType;
		selectionStyle: Record<string, string | number>;
		currentMediaQuery: StoryMediaPlatform;
		selectionGroup: Props['selection']['clicked'];
		isMultiple: boolean;
		boundaries: ResponsiveProviderContextType['boundaries'];
	}) => {
		if (
			type === SELECTION_TYPES.hovered ||
			isSelection.typeOf(selection, COMPONENT_TYPE.CONTENT) ||
			isSelection.typeOf(selection, COMPONENT_TYPE.CARD)
		) {
			return null;
		}

		const isHiddenPositionPoints = Boolean(this.state.dragProp) && this.state.dragProp !== SUPPORTED_PROP.position;
		const isHiddenSizePoints = Boolean(this.state.dragProp) && this.state.dragProp !== SUPPORTED_PROP.size;

		const isStackChild = isChildOf(selection, BOX_TYPE.STACK);
		const lockX = _.get(selection, 'uiConfig.componentProps.styles.justifySelf') === 'stretch' || isStackChild;
		const lockY = _.get(selection, 'uiConfig.componentProps.styles.alignSelf') === 'stretch';
		const lockH =
			isStack(selection) ||
			isGrid(selection) ||
			isBBType(selection, COMPONENT_TYPE.TEXT) ||
			isBBType(selection, COMPONENT_TYPE.RESULT_TEXT) ||
			isBBType(selection, COMPONENT_TYPE.CHECKBOX);

		if (isChildOf(selection, BOX_TYPE.GRID)) {
			return null;
		}

		return (
			<React.Fragment key={selection.id}>
				<Points
					hidden={isHiddenPositionPoints}
					name={SUPPORTED_PROP.position}
					style={selectionStyle as any}
					selection={selection}
					selectionGroup={selectionGroup}
					isMultiple={isMultiple}
					setDragging={this.setDragging}
					currentState={this.props.currentState}
					currentMediaQuery={currentMediaQuery}
					lockX={lockX}
					lockY={lockY}
					lockH={lockH}
					boundaries={boundaries}
				/>
				{!isMultiple && selection.type !== COMPONENT_TYPE.GOOGLE_LOGIN && (
					<Points
						hidden={isHiddenSizePoints}
						name={SUPPORTED_PROP.size}
						style={selectionStyle as any}
						selection={selection}
						selectionGroup={selectionGroup}
						isMultiple={isMultiple}
						setDragging={this.setDragging}
						currentState={this.props.currentState}
						currentMediaQuery={currentMediaQuery}
						lockX={lockX}
						lockY={lockY}
						lockH={lockH}
						boundaries={boundaries}
					/>
				)}
			</React.Fragment>
		);
	};

	renderSelection = ({
		type,
		selectionStyle,
		selection,
		currentMediaQuery,
		position,
		selectionGroup,
		isMultiple,
		boundaries,
	}: {
		type: keyof Props['selection'];
		selectionStyle: Record<string, string | number>;
		selection: SelectionSingleType;
		currentMediaQuery: StoryMediaPlatform;
		position: CSSStyleDeclaration['position'];
		selectionGroup: Props['selection']['clicked'];
		isMultiple: boolean;
		boundaries: ResponsiveProviderContextType['boundaries'];
	}) => {
		const isRenderBreadcrumbs = !isMultiple && !this.state.dragProp;
		const isHiddenSelection = this.state.dragProp === SUPPORTED_PROP.position && this.state.dragId !== selection.id;
		const isHiddenSelectionRect = Boolean(this.state.dragProp);

		return (
			<div
				className={css.selectionW}
				data-selection-position={position}
				data-selection-type={type}
				key={`selection-${type}-${selection.id}`}
				hidden={isHiddenSelection}
			>
				<div
					className={cn(css.selection, `selection-${type}`, {
						[css.selectionAccent]:
							type === SELECTION_TYPES.clicked && selection.selectionStage === SELECTION_STAGE.INNER,
					})}
					style={selectionStyle}
					hidden={isHiddenSelectionRect}
				>
					{isRenderBreadcrumbs && <Breadcrumbs selection={selection} />}
				</div>
				{this.renderPoints({
					type,
					selection,
					selectionStyle,
					currentMediaQuery,
					isMultiple,
					selectionGroup,
					boundaries,
				})}
			</div>
		);
	};

	renderSelections = ({
		currentMediaQuery,
		boundaries,
	}: {
		currentMediaQuery: StoryMediaPlatform;
		boundaries: ResponsiveProviderContextType['boundaries'];
	}) => {
		const clickedSelections = this.props.selection[SELECTION_TYPES.clicked];
		const isGroupSelected = _.size(clickedSelections) > 1;

		return (Object.keys(this.props.selection) as (keyof Props['selection'])[]).map(type => {
			const values = this.props.selection[type];
			const [id, selection] = Object.entries(values)[0] || [];

			const node = document.getElementById(id);
			const isSkipRender =
				!node ||
				this.state.isScrolling ||
				// is hovered node already clicked
				(type === SELECTION_TYPES.hovered && this.props.selection[SELECTION_TYPES.clicked][id]);

			if (isSkipRender) {
				return null;
			}

			const isGroup = type === SELECTION_TYPES.clicked && isGroupSelected;
			const selectionStyle = isGroup ? getSelectionGroupPosition(clickedSelections) : getSelectionPosition(node);
			let position: CSSStyleDeclaration['position'] = 'relative';

			if (isGroup) {
				position = selectionStyle.position;
			} else if (node) {
				position = window.getComputedStyle(node).position;
			}

			return this.renderSelection({
				currentMediaQuery,
				type,
				selection,
				selectionGroup: clickedSelections,
				selectionStyle,
				position,
				isMultiple: isGroup,
				boundaries,
			});
		});
	};

	render() {
		return (
			<ResponsiveProviderContext.Consumer>
				{({ currentMediaQuery, boundaries }) =>
					currentMediaQuery &&
					boundaries &&
					createPortal(this.renderSelections({ currentMediaQuery, boundaries }), document.body)
				}
			</ResponsiveProviderContext.Consumer>
		);
	}
}

const mapStateToProps = (state: ClientReducerState) => ({
	selection: state.editor.selection,
	currentState: state.editor.state.state,
});
const mapDispatchToProps = {
	setEditorSelectedNode,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

export default connector(SelectionHint);
